import { getAmplitudeInstance } from '../amplitudeHelpers';
import ActivityShareEntryPoint from '../enums/ActivityShareEntryPoint';
import ActivityType from '../enums/ActivityType';
import NavigatedFrom from '../enums/NavigatedFrom';
import RecapPage from '../enums/RecapPage';
import ShareLocation from '../enums/ShareLocation';
import ShareSource from '../enums/ShareSource';
import TrailPageType from '../enums/TrailPageType';

type logShareDisplayedParams = {
  activity_distance_miles?: number;
  activity_elevation_gain_feet?: number;
  activity_moving_time_duration_seconds?: number;
  activity_type?: ActivityType;
  entry_point?: ActivityShareEntryPoint;
  id?: string;
  inspirational_list_type?: string;
  navigated_from?: NavigatedFrom;
  num_pages_available?: number;
  num_park_stamps?: number;
  owned_by_user?: boolean;
  owner_id?: string;
  page_slug?: string;
  recap_frame?: RecapPage;
  recording_id?: string;
  share_location?: ShareLocation;
  source?: ShareSource;
  trail_page_type?: TrailPageType;
};

/**
 * User clicks a share action. Beginning in June 2026 this action from the trail page triggers the display of a module with multiple trail assets for sharing.
 * @param params - Attributes to log with the amplitude event
 * @param params.activity_distance_miles - If sharing activity, activity distance in miles.
 * @param params.activity_elevation_gain_feet - If sharing activity, activity elevation gain in feet of the recording.
 * @param params.activity_moving_time_duration_seconds - If sharing activity, total moving time duration in seconds of the activity.
 * @param params.activity_type - User's selected activity. Equivalent to the 'selected activity uid' attribute on the navigator activity selected event.
 * @param params.entry_point - The CTA or surface from which the user reached the activity share carousel. Populated for post-activity share only.
 * @param params.id - the remote id of the object being shared.
 * @param params.inspirational_list_type - List type of the page (programmatic list, sponsored list, trail, etc,), passed from server
 * @param params.navigated_from - The location the user navigated from to get to this content
 * @param params.num_pages_available - Number of possible cover pages in the share carousel
 * @param params.num_park_stamps - The number of stamps the user has unlocked for their national park stamp book.
 * @param params.owned_by_user - Whether the screen is associated a first party or third party user.
 * @param params.owner_id - User ID of the content owner.
 * @param params.page_slug - Page slug shared by user
 * @param params.recap_frame - If the user taps share from one of the recap screens while the recap is taking place, this field is populated with the frame that was on screen at the time of the action being performed. e.g. flyover or photo asset.
 * @param params.recording_id - Unique ID for the recording if user is sharing an activity/recap.
 * @param params.share_location - the location where the share button or CTA is located.
 * @param params.source - the location from which share was initiated, also object type.
 * @param params.trail_page_type - Type of trail page where the share CTA was clicked
 */
const logShareDisplayed = (
  {
    activity_distance_miles,
    activity_elevation_gain_feet,
    activity_moving_time_duration_seconds,
    activity_type,
    entry_point,
    id,
    inspirational_list_type,
    navigated_from,
    num_pages_available,
    num_park_stamps,
    owned_by_user,
    owner_id,
    page_slug,
    recap_frame,
    recording_id,
    share_location,
    source,
    trail_page_type
  }: logShareDisplayedParams,
  callback?: () => void
) => {
  getAmplitudeInstance().logEvent(
    'shared displayed',
    {
      'activity distance miles': activity_distance_miles,
      'activity elevation gain feet': activity_elevation_gain_feet,
      'activity moving time duration seconds': activity_moving_time_duration_seconds,
      'activity type': activity_type,
      'entry point': entry_point,
      id,
      'inspirational list type': inspirational_list_type,
      'navigated from': navigated_from,
      'num pages available': num_pages_available,
      'num park stamps': num_park_stamps,
      'owned by user': owned_by_user,
      'owner id': owner_id,
      'page slug': page_slug,
      'recap frame': recap_frame,
      'recording id': recording_id,
      'share location': share_location,
      source,
      'trail page type': trail_page_type
    },
    callback
  );
};

export default logShareDisplayed;
